import {
  SaveMessageTemplate,
  ApplyTemplateToDraft,
} from './EmailMessageActions';
import { useEffect, useRef, useState } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  emailApi,
  emailKey,
  type EmailDetail,
  type EmailThread,
} from '../../lib/email-api';
import styles from './Emails.module.css';
import { SwipeCard, type CardPreview } from './SwipeCard';
import { MailSurface, MailMessage, MailSubject, MailComposer } from './MailSurface';
import mailStyles from './MailSurface.module.css';
import { useGmailConnection, GmailConnectionNotice } from './GmailConnection';
const friendly = (value: string) => ({ queued: 'Preparing your reply…', generating: 'Writing your reply…', draft: 'Ready for review', draft_ready: 'Ready for review', no_reply_needed: 'No reply needed', drafting_paused: 'Automatic reply preparation is paused', ready: 'Ready for review', prepared: 'Sending…', submitting: 'Sending…', sent: 'Sent', unknown: 'Send status unknown', paused_budget: 'AI spending limit reached', changed_since_draft: 'New email arrived — review before sending', no_reply: 'No reply needed', usage_unknown: 'AI usage needs review', user_confirmed_sent: 'Marked sent by you' }[value] ?? value.replace(/_/g, ' '));

export function Emails({ recordId, onThreadSelected }: { recordId?: string; onThreadSelected?: (id: string | undefined) => void }) {
  const client = useQueryClient();
  const key = emailKey();
  const connections = useGmailConnection();
  const [run, setRun] = useState<string>();
  const [index, setIndex] = useState(0);
  const queue = useInfiniteQuery({
    queryKey: [
      ...key,
      'queue',
      recordId ?? 'brief',
      connections.data?.connections[0]?.id,
    ],
    queryFn: ({ pageParam }) => emailApi.list(recordId, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled: !!connections.data?.connections.some(c => c.status !== 'disconnecting'),
    retry: false,
    refetchInterval: (q) =>
      q.state.status !== 'error' && connections.data?.enabled ? 30_000 : false,
  });
  const progress = useQuery({
    queryKey: [...key, 'sync', run],
    queryFn: () => emailApi.syncRun(run!),
    enabled: !!run,
    retry: false,
    refetchInterval: (q) =>
      q.state.status !== 'error' &&
      ['queued', 'running'].includes(q.state.data?.status ?? 'queued')
        ? 1500
        : false,
  });
  const sync = useMutation({
    mutationFn: (id: string) => emailApi.sync(id),
    onSuccess: (result) => {
      setRun(result.syncRunId);
      void client.invalidateQueries({ queryKey: key });
    },
  });
  const requestedThreadId = new URLSearchParams(window.location.search).get(
    'emailThreadId',
  );
  const requested = useQuery({
    queryKey: [...key, 'thread-link', requestedThreadId],
    queryFn: () => emailApi.thread(requestedThreadId!),
    enabled: !!requestedThreadId && !!connections.data?.connections.some(c => c.status !== 'disconnecting'),
    retry: false,
  });
  const queueCards = queue.data?.pages.flatMap((page) => page.items) ?? [];
  const linked = requested.data;
  const cards: EmailThread[] =
    linked && !queueCards.some((c) => c.id === linked.id)
      ? [
          {
            id: linked.id,
            connectionId: linked.connectionId,
            revision: linked.revision,
            subject: linked.subject,
            lastActivityAt: '',
            unreadCount: linked.messages.filter((m) =>
              m.labels.includes('UNREAD'),
            ).length,
            gmailUrl: linked.gmailUrl,
            review: linked.review,
          },
          ...queueCards,
        ]
      : queueCards;
  // Keep stable identity while new activity arrives or the user edits.
  const [selected, setSelected] = useState<string | undefined>(
    new URLSearchParams(window.location.search).get('emailThreadId') ??
      undefined,
  );
  const active =
    cards.find((c) => c.id === selected) ??
    cards[Math.min(index, Math.max(0, cards.length - 1))];
  useEffect(() => {
    if (active && active.id !== selected) setSelected(active.id);
  }, [active?.id, selected]);
  useEffect(() => { onThreadSelected?.(active?.id); }, [active?.id, onThreadSelected]);
  useEffect(() => {
    if (progress.data && !['queued', 'running'].includes(progress.data.status))
      void client.invalidateQueries({ queryKey: key });
  }, [progress.data?.status]);
  if (connections.isLoading) return null;
  if (connections.isError) return recordId ? <GmailConnectionNotice /> : null;
  const settings = connections.data!;
  if (!settings.connections.length) return recordId ? <GmailConnectionNotice /> : null;
  if (settings.connections.every(c => c.status === 'disconnecting')) return null;
  const refreshing = sync.isPending || (!!run && !progress.isError && ['queued', 'running'].includes(progress.data?.status ?? 'queued'));
  return (
    <section className={styles.emails} aria-label="Emails">
      {recordId && <header><h2>Emails</h2>
        {settings.connections.map(c => <button key={c.id} disabled={refreshing || !settings.enabled || !['active', 'importing'].includes(c.status)} onClick={() => sync.mutate(c.id)}>{refreshing ? 'Refreshing…' : 'Refresh'}</button>)}
      </header>}
      {settings.connections.some(c => ['paused', 'reconnect_required'].includes(c.status)) && <p>Gmail needs attention. <a href="/app/settings/gmail">Manage connection</a></p>}
      {settings.sendReceipts?.filter(a => a.status === 'unknown').map(a => <UnknownReceipt key={a.id} id={a.id} createdAt={a.createdAt} />)}
      {recordId && settings.eligibleAddressCount === 0 && cards.length === 0 && <p>Add an email address to {settings.policy === 'people_only' ? 'a Person' : 'a record'} to see their conversations here.</p>}
      {(sync.error || progress.error || progress.data?.lastError) && <p role="alert">{sync.error?.message ?? progress.error?.message ?? 'Gmail couldn’t finish refreshing. Try again.'}</p>}
      <>
          {requested.error && <p role="alert">{requested.error.message}</p>}
          {queue.isError ? (
            <p role="alert">
              Email history could not be loaded.{' '}
              <button onClick={() => void queue.refetch()}>Try again</button>
            </p>
          ) : recordId && !queue.isLoading &&
            settings.connections.length > 0 &&
            cards.length === 0 ? (
            <p>
              {recordId
                ? 'No permitted conversations imported for this record yet.'
                : 'No email waiting for review. Your history stays on record pages.'}
            </p>
          ) : null}
          {active && (
            <div>
              <nav aria-label="Email cards">
                <button
                  disabled={cards[0]?.id === active.id}
                  onClick={() => {
                    const n = cards.findIndex((c) => c.id === active.id) - 1;
                    setIndex(n);
                    setSelected(cards[n]?.id);
                  }}
                >
                  Previous
                </button>
                <span>
                  {cards.findIndex((c) => c.id === active.id) + 1} of{' '}
                  {cards.length}{queue.hasNextPage ? '+' : ''}
                </span>
                <button
                  disabled={cards.at(-1)?.id === active.id}
                  onClick={() => {
                    const n = cards.findIndex((c) => c.id === active.id) + 1;
                    setIndex(n);
                    setSelected(cards[n]?.id);
                  }}
                >
                  Next
                </button>
              </nav>
              <EmailCard
                key={active.id}
                thread={active}
                previews={recordId ? [] : cards.slice(cards.findIndex(c => c.id === active.id) + 1, cards.findIndex(c => c.id === active.id) + 3).map(c => ({ title: c.subject || 'Email', caption: 'For review' }))}
                sendsEnabled={settings.sendsEnabled}
                draftingEnabled={settings.draftingEnabled}
                mutationsEnabled={
                  settings.enabled &&
                  settings.connections.some(
                    (c) =>
                      c.id === active.connectionId && c.status === 'active',
                  )
                }
              />
            </div>
          )}
          {queue.hasNextPage && (
            <button
              disabled={queue.isFetchingNextPage}
              onClick={() => void queue.fetchNextPage()}
            >
              Load more conversations
            </button>
          )}
      </>
    </section>
  );
}
/** Both chat and Brief read the live saved draft and use identical approval controls. */
export function ChatEmailCard({ threadId }: { threadId: string }) {
  const connections = useGmailConnection();
  const detail = useQuery({
    queryKey: [...emailKey(), 'chat-card', threadId],
    queryFn: () => emailApi.thread(threadId),
    retry: false,
  });
  if (detail.isLoading || connections.isLoading) return <p role="status">Loading email…</p>;
  if (detail.isError) return <p role="alert">{detail.error.message} <button onClick={() => void detail.refetch()}>Try again</button></p>;
  if (!detail.data || !connections.data) return <GmailConnectionNotice />;
  const data = detail.data;
  return <EmailCard thread={{ ...data, lastActivityAt: '', unreadCount: data.messages.filter(m => m.labels.includes('UNREAD')).length }}
    sendsEnabled={connections.data.sendsEnabled}
    draftingEnabled={connections.data.draftingEnabled}
    mutationsEnabled={connections.data.enabled && connections.data.connections.some(c => c.id === data.connectionId && c.status === 'active')} />;
}

export function UnknownReceipt({ id, createdAt }: { id: string; createdAt: string }) {
  const client = useQueryClient();
  const resolve = useMutation({
    mutationFn: (disposition: 'sent' | 'not_sent') =>
      emailApi.resolve(id, disposition),
    onSuccess: () => client.invalidateQueries({ queryKey: emailKey() }),
  });
  return (
    <details>
      <summary>
        An approved send from {new Date(createdAt).toLocaleString()} has unknown
        status
      </summary>
      <p>
        Check the connected account’s Sent folder before resolving this receipt.
        This records your confirmation and never sends another message.
      </p>
      <button
        disabled={resolve.isPending}
        onClick={() => resolve.mutate('sent')}
      >
        I checked Gmail and confirmed sent
      </button>
      <button
        disabled={resolve.isPending}
        onClick={() => resolve.mutate('not_sent')}
      >
        I checked Gmail and confirmed not sent
      </button>
      {resolve.error && <p role="alert">{resolve.error.message}</p>}
    </details>
  );
}
function EmailCard({
  thread,
  sendsEnabled,
  draftingEnabled,
  mutationsEnabled,
  previews = [],
}: {
  previews?: CardPreview[];
  thread: EmailThread;
  sendsEnabled: boolean;
  draftingEnabled: boolean;
  mutationsEnabled: boolean;
}) {
  const key = emailKey(),
    client = useQueryClient();
  const detail = useQuery({
    queryKey: [...key, 'thread', thread.connectionId, thread.id],
    queryFn: () => emailApi.thread(thread.id),
    retry: false,
    refetchInterval: (q) =>
      q.state.status !== 'error' && mutationsEnabled ? (['queued', 'generating'].includes(q.state.data?.review?.generation?.status ?? '') || ['prepared', 'submitting'].includes(q.state.data?.review?.sendAttempt?.status ?? '') ? 2000 : 30_000) : false,
  });
  const [extra, setExtra] = useState<EmailDetail['messages']>([]),
    [cursor, setCursor] = useState<string | null | undefined>();
  const loadMore = useMutation({
    mutationFn: () =>
      emailApi.thread(
        thread.id,
        cursor ?? detail.data?.nextCursor ?? undefined,
      ),
    onSuccess: (data) => {
      setExtra((prev) => [...prev, ...data.messages]);
      setCursor(data.nextCursor);
    },
  });
  const review = detail.data?.review ?? thread.review,
    draft = review?.draft;
  const editorKey = [...key, 'editor', thread.connectionId, thread.id];
  const cached = client.getQueryData<{
    to: string;
    cc: string;
    subject: string;
    body: string;
    base: { draftRevision: number; sourceRevision: number };
  }>(editorKey);
  const [editing, setEditing] = useState(!!cached),
    [to, setTo] = useState(cached?.to ?? ''),
    [cc, setCc] = useState(cached?.cc ?? ''),
    [subject, setSubject] = useState(cached?.subject ?? ''),
    [body, setBody] = useState(cached?.body ?? '');
  const base = useRef<
    { draftRevision: number; sourceRevision: number } | undefined
  >(cached?.base);
  useEffect(() => {
    if (editing && base.current)
      client.setQueryData(editorKey, {
        to,
        cc,
        subject,
        body,
        base: base.current,
      });
    else client.removeQueries({ queryKey: editorKey, exact: true });
  }, [editing, to, cc, subject, body]);
  const sendKeys = useRef(new Map<string, string>());
  const [showSchedule, setShowSchedule] = useState(false);
  const [sendAt, setSendAt] = useState('');
  const [replyAll, setReplyAll] = useState(false),
    [instructions, setInstructions] = useState('');
  const messages = [...(detail.data?.messages ?? []), ...extra];
  const unreadIncoming = messages.filter(m => m.direction !== 'sent' && m.labels.includes('UNREAD'));
  const action = useMutation({
    mutationFn: async (kind: string) => {
      if (kind === 'cancel_schedule' && review?.sendAttempt)
        return emailApi.cancelSchedule(review.sendAttempt.id);
      if (kind === 'resolve_sent' && review?.sendAttempt)
        return emailApi.resolve(review.sendAttempt.id, 'sent');
      if (kind === 'resolve_not_sent' && review?.sendAttempt)
        return emailApi.resolve(review.sendAttempt.id, 'not_sent');
      if (kind === 'read')
        return emailApi.markRead(
          thread.id,
          unreadIncoming.map((m) => m.id),
        );
      if (kind === 'done' && review)
        return emailApi.done(review.id, review.revision);
      if (kind === 'draft')
        return emailApi.draft(thread.id, replyAll, instructions);
      if (kind === 'save' && draft && base.current) {
        const saved = await emailApi.edit(draft.id, {
          expectedRevision: base.current.draftRevision,
          expectedSourceRevision: base.current.sourceRevision,
          to: to
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean),
          cc: cc
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean),
          subject,
          bodyText: body,
        });
        setEditing(false);
        return saved;
      }
      if ((kind === 'send' || kind === 'schedule') && draft && review) {
        const scheduled = kind === 'schedule' ? new Date(sendAt) : null;
        if (scheduled && (!Number.isFinite(scheduled.getTime()) || scheduled.getTime() <= Date.now()))
          throw new Error('Choose a future date and time.');
        const request = `${draft.id}:${scheduled?.toISOString() ?? 'now'}`;
        const key = sendKeys.current.get(request) ?? crypto.randomUUID();
        sendKeys.current.set(request, key);
        return emailApi.send(draft.id, {
          expectedDraftRevision: draft.revision,
          expectedSourceRevision: draft.sourceRevision,
          expectedReviewRevision: review.revision,
          idempotencyKey: key,
          ...(scheduled ? { sendAt: scheduled.toISOString() } : {}),
        });
      }
    },
    onSuccess: (result, kind) => {
      if ((kind === 'send' || kind === 'schedule') && result && typeof result === 'object' && 'status' in result && 'id' in result) {
        const receipt = result as { id: string; status: string; sendAt?: string | null };
        // Use the actual send response immediately; don't wait for a second
        // request to replace the composer or briefly offer Send again.
        client.setQueryData<EmailDetail>([...key, 'thread', thread.connectionId, thread.id], previous =>
          previous?.review ? { ...previous, review: { ...previous.review,
            sendAttempt: { ...receipt, lastError: null },
          } } : previous);
      }
      if (kind === 'cancel_schedule') {
        setShowSchedule(false);
        beginEdit();
      }
      return client.invalidateQueries({ queryKey: key });
    },
  });
  const beginEdit = () => {
    if (!draft || !detail.data) return;
    base.current = {
      draftRevision: draft.revision,
      sourceRevision: detail.data.revision,
    };
    setTo(draft.to.join(', '));
    setCc(draft.cc.join(', '));
    setSubject(draft.subject);
    setBody(draft.bodyText);
    setEditing(true);
  };
  const pending = action.isPending;
  const sending =
    review?.sendAttempt &&
    [
      'prepared',
      'scheduled',
      'submitting',
      'unknown',
      'sent',
      'user_confirmed_sent',
    ].includes(review.sendAttempt.status);
  const sent = ['sent', 'user_confirmed_sent'].includes(review?.sendAttempt?.status ?? '');
  const scheduled = review?.sendAttempt?.status === 'scheduled';
  const submitting = !sent && ((pending && action.variables === 'send') ||
    ['prepared', 'submitting'].includes(review?.sendAttempt?.status ?? ''));
  if (draft && (sent || submitting || scheduled)) {
    return <MailSurface label={sent ? 'Sent email' : scheduled ? 'Scheduled email' : 'Sending email'} account={draft.from} gmailUrl={detail.data?.gmailUrl ?? thread.gmailUrl}>
      <article className={styles.receipt}>
        <div className={styles.delivery} data-state={sent ? 'sent' : scheduled ? 'scheduled' : 'sending'} role="status" aria-live="polite">
          <span className={styles.deliveryIcon} aria-hidden="true">
            {sent ? <svg viewBox="0 0 24 24" fill="none"><path d="m5 12 4 4L19 6" /></svg> : scheduled ? <span>◷</span> : <span className={styles.sendSpinner} />}
          </span>
          <div><strong>{sent ? review?.sendAttempt?.status === 'user_confirmed_sent' ? 'Marked sent by you' : 'Sent via Gmail' : scheduled ? 'Scheduled to send' : 'Sending via Gmail…'}</strong>
            <p>To {draft.to.join(', ')}{draft.cc.length > 0 ? ` · Cc ${draft.cc.join(', ')}` : ''}</p>
            {scheduled && review?.sendAttempt?.sendAt && <p>{new Date(review.sendAttempt.sendAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })} · {Intl.DateTimeFormat().resolvedOptions().timeZone}</p>}
          </div>
        </div>
        <h3 className={styles.receiptSubject}>{draft.subject || '(No subject)'}</h3>
        <details className={styles.receiptMessage}><summary>View {sent ? 'sent email' : 'message'}</summary><p className={styles.body}>{draft.bodyText}</p></details>
        {scheduled && <button type="button" disabled={pending || !mutationsEnabled} onClick={() => action.mutate('cancel_schedule')}>Cancel schedule &amp; edit</button>}
        {action.error && <p role="alert">{action.error.message}</p>}
      </article>
    </MailSurface>;
  }
  const canSend = !pending && !editing && !sending && review?.status === 'open' && draft?.status === 'ready' && review?.sendability?.allowed !== false && sendsEnabled && mutationsEnabled && !!detail.data;
  const canReject = !pending && !editing && !sending && review?.status === 'open';
  return (
    <SwipeCard itemKey={`${thread.id}:${review?.id}:${review?.revision}:${draft?.id}:${draft?.revision}:${draft?.sourceRevision}`} previews={previews} onRight={canSend ? () => action.mutate('send') : undefined} onLeft={canReject ? () => action.mutate('done') : undefined}>
    {({ right, left }) => <MailSurface label={review?.kind === 'outreach' ? 'Prepared email' : 'Reply for review'} account={draft?.from} gmailUrl={thread.gmailUrl}>
    <article className={styles.card}>
      <p className={styles.kind}>
        {review?.kind === 'outreach'
          ? review?.provenance?.origin === 'automation'
            ? 'Automation-prepared email'
          : review?.provenance?.origin === 'chat_compose' ? 'Email prepared in chat' : 'Suggested outreach'
          : 'Incoming conversation'}
      </p>
      {review?.provenance?.origin === 'automation' && (
        <p>
          <a href={`/app/email/automations/${review.provenance.automationId}`}>
            {review.provenance.automationName}
          </a>{' '}
          · Step {review.provenance.stepNumber} ·{' '}
          <a href={`/app/email/enrollments/${review.provenance.enrollmentId}`}>
            Run status
          </a>
        </p>
      )}
      {draft?.provenance?.renderMode && (
        <p>
          {draft.provenance.editedFromTemplate
            ? 'Edited from template'
            : draft.provenance.renderMode === 'exact'
              ? 'Filled from template'
              : 'AI draft'}
          {draft.provenance.templateId && (
            <>
              {' '}
              ·{' '}
              <a href={`/app/email/templates/${draft.provenance.templateId}`}>
                Template v{draft.provenance.templateVersion}
              </a>
            </>
          )}
        </p>
      )}
      <MailSubject subject={draft?.subject || thread.subject || '(No subject)'} />
      {thread.records?.length ? (
        <p>
          {thread.records
            .map((r) => `${r.displayName} · ${r.address}`)
            .join('; ')}
        </p>
      ) : null}
      {detail.data?.recentContext?.length ? (
        <details>
          <summary>Recent correspondence for this outreach</summary>
          {detail.data.recentContext.map((m, i) => (
            <section className={styles.message} key={i}>
              <p>
                {m.sender.map((s) => s.address).join(', ')} ·{' '}
                {new Date(m.receivedAt).toLocaleString()}
              </p>
              <p className={styles.body}>{m.bodyText}</p>
            </section>
          ))}
        </details>
      ) : null}
      <p role="status">
        {friendly(
          review?.sendAttempt?.status ??
            (draft?.status === 'stale'
              ? 'changed_since_draft'
              : review?.generation &&
                  [
                    'queued',
                    'generating',
                    'paused_budget',
                    'failed',
                    'usage_unknown',
                  ].includes(review.generation.status)
                ? review.generation.status
                : (review?.outcome ?? 'History')),
        )}
        {review?.reason ? ` · ${review.reason}` : ''}
      </p>
      {review?.sendability && !review.sendability.allowed && (
        <p role="status">
          Sequence cannot send: {friendly(review.sendability.reason)}. Open its
          run to review.
        </p>
      )}
      {review?.sendAttempt?.status === 'unknown' && (
        <div>
          <p>
            Send status unknown. Check Gmail; Resonance will reconcile. Sending
            again could create a duplicate.
          </p>
          <details>
            <summary>Resolve after checking Gmail</summary>
            <p>
              Only resolve this after checking the connected account’s Sent
              folder. This records your confirmation; it does not submit another
              email.
            </p>
            <button
              disabled={pending}
              onClick={() => action.mutate('resolve_sent')}
            >
              I confirmed it was sent
            </button>
            <button
              disabled={pending}
              onClick={() => action.mutate('resolve_not_sent')}
            >
              I confirmed it was not sent
            </button>
          </details>
        </div>
      )}
      {review?.sendAttempt && ['failed', 'cancelled', 'user_confirmed_not_sent'].includes(review.sendAttempt.status) &&
        <p role="alert">This email was not sent. Your draft is saved. Review and save the draft before trying again.</p>}
      {detail.isError && <p role="alert">Conversation could not be loaded.</p>}
      {detail.data?.missingHistory && <p>Older history remains in Gmail.</p>}
      {detail.data && !detail.data.healthy && (
        <p>This conversation could not be refreshed completely.</p>
      )}
      {messages.length > 0 && <details open className={styles.conversation}>
        <summary>Conversation{unreadIncoming.length > 0 ? ` · ${unreadIncoming.length} unread` : ''}</summary>
        {messages.map((message) => (
          <MailMessage key={message.id}
            from={message.sender.map(a => a.name ? `${a.name} <${a.address}>` : a.address).join(', ')}
            to={message.recipients.map(a => a.address).join(', ')}
            subject={message.subject}
            timestamp={new Date(message.receivedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
            body={message.bodyText}>
            {message.truncated && <p>Message truncated; read the full email in Gmail.</p>}
            {message.attachments.map((a, i) => <p key={i}>Attachment: {a.name} ({a.size} bytes) · Open in Gmail</p>)}
          </MailMessage>
        ))}
        {(cursor === undefined ? detail.data?.nextCursor : cursor) && (
          <button
            disabled={loadMore.isPending}
            onClick={() => loadMore.mutate()}
          >
            Load more messages
          </button>
        )}
        {unreadIncoming.length > 0 && <button
          disabled={pending || !mutationsEnabled}
          onClick={() => action.mutate('read')}
        >
          Mark as read
        </button>}
      </details>}
      {draft && review?.sendAttempt?.status === 'unknown' && <MailMessage from={draft.from} to={draft.to.join(', ')} subject={draft.subject} body={draft.bodyText} />}
      {draft && !sending && <MailComposer
        to={editing ? to : draft.to.join(', ')}
        body={editing ? body : draft.bodyText}
        ai={draft.provenance?.renderMode !== 'exact'}
        edited={editing || draft.edited}
        disabled={pending || !!sending || !detail.data || !mutationsEnabled || !['ready', 'stale', 'send_failed'].includes(draft.status)}
        onChange={value => { if (!editing) beginEdit(); setBody(value); }}
        fields={<details className={styles.recipientDetails}>
          <summary>Recipients &amp; subject</summary>
          <div className={styles.editorFields}>
            <label>To<input value={editing ? to : draft.to.join(', ')}
              disabled={pending || !!sending || !detail.data || !mutationsEnabled || !['ready', 'stale', 'send_failed'].includes(draft.status)}
              onChange={e => { if (!editing) beginEdit(); setTo(e.target.value); }} /></label>
            <label>Cc<input value={editing ? cc : draft.cc.join(', ')}
              disabled={pending || !!sending || !detail.data || !mutationsEnabled || !['ready', 'stale', 'send_failed'].includes(draft.status)}
              onChange={e => { if (!editing) beginEdit(); setCc(e.target.value); }} /></label>
            <label>Subject<input value={editing ? subject : draft.subject}
              readOnly={draft.provenance?.threadMode === 'reply_previous'}
              disabled={pending || !!sending || !detail.data || !mutationsEnabled || !['ready', 'stale', 'send_failed'].includes(draft.status)}
              onChange={e => { if (!editing) beginEdit(); setSubject(e.target.value); }} /></label>
          </div>
        </details>}
        actions={<>
          {review?.status === 'open' && <button type="button" className={mailStyles.dismiss}
            disabled={pending || editing || !!sending}
            title={review.provenance?.origin === 'automation' ? 'Dismiss and stop this sequence' : 'Dismiss this review card'}
            onClick={left}>Dismiss</button>}
          <span className={mailStyles.sendGroup}>
            <button type="button" className={mailStyles.primary}
              disabled={!canSend}
              title={editing ? 'Save your reviewed changes before sending' : 'Send this email from Gmail'}
              onClick={right}>Send</button>
            <button type="button" className={mailStyles.schedule} disabled={!canSend} onClick={() => setShowSchedule(!showSchedule)}>Schedule send</button>
          </span>
        </>}>
        {showSchedule && <div className={styles.schedule}>
          <label>Send date and time
            <input type="datetime-local" value={sendAt} disabled={!canSend} onChange={event => setSendAt(event.target.value)} />
          </label>
          <p>{Intl.DateTimeFormat().resolvedOptions().timeZone} · Approves this exact email to send at the chosen time.</p>
          <div>
            <button type="button" disabled={!canSend || !sendAt} onClick={() => action.mutate('schedule')}>{pending && action.variables === 'schedule' ? 'Scheduling…' : 'Confirm scheduled send'}</button>
            <button type="button" disabled={pending} onClick={() => setShowSchedule(false)}>Cancel</button>
          </div>
        </div>}
        {editing && <div className={styles.editChanges}>
          <span>Unsaved changes</span>
          <button disabled={pending || !!sending || !mutationsEnabled} onClick={() => action.mutate('save')}>Save reviewed draft</button>
          <button disabled={pending} onClick={() => setEditing(false)}>Cancel edits</button>
          {action.error && <>
            <p>Your words are kept here. Review the latest conversation before retrying.</p>
            {detail.data && <button disabled={pending} onClick={() => {
              base.current = { draftRevision: draft.revision, sourceRevision: detail.data!.revision };
              action.reset();
            }}>Keep my edits after reviewing the latest conversation</button>}
          </>}
        </div>}
        <details className={styles.templates}><summary>Templates</summary>
          <SaveMessageTemplate subject={editing ? subject : draft.subject} bodyText={editing ? body : draft.bodyText}
            addresses={editing ? to.split(',').map(value => value.trim()).filter(Boolean) : draft.to}
            personalFacts={thread.records?.map(r => r.displayName) ?? []} />
          {!editing && !sending && <ApplyTemplateToDraft connectionId={thread.connectionId} draft={draft} records={thread.records ?? []} />}
        </details>
      </MailComposer>}
      {review?.generation?.replacement?.outcome === 'draft' && (
        <details>
          <summary>Proposed replacement</summary>
          <p className={styles.body}>
            {review.generation.replacement.bodyText}
          </p>
          <button
            disabled={pending || editing || !!sending}
            onClick={() => {
              beginEdit();
              setBody(review.generation!.replacement!.bodyText ?? '');
            }}
          >
            Review replacement
          </button>
        </details>
      )}
      {review?.provenance?.origin !== 'chat_compose' && <details className={styles.instructions}><summary>Refine with Echo</summary>
      <label>
        Facts or instructions for a new draft (optional)
        <textarea
          maxLength={2000}
          rows={2}
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          placeholder="For example, the times you are available"
        />
      </label>
        <label><input type="checkbox" checked={replyAll} onChange={e => setReplyAll(e.target.checked)} />Reply all for next draft</label>
        <button disabled={pending || editing || !!sending || !draftingEnabled || !mutationsEnabled} onClick={() => action.mutate('draft')}>{draft ? 'Prepare another reply' : 'Prepare reply'} · uses AI credits</button>
      </details>}
      {!draft && review?.status === 'open' && <div className={styles.actions}>
        <button disabled={pending || !!sending} onClick={left}
          title={review.provenance?.origin === 'automation' ? 'Dismiss and stop this sequence' : 'Dismiss this review card'}>Dismiss</button>
      </div>}
      {action.error && <p role="alert">{action.error.message}</p>}
    </article>
    </MailSurface>}
    </SwipeCard>
  );
}
